import { useEffect, useRef, useState } from "preact/hooks"
import { AlertCircle, BarChart3, CheckCircle2, Loader2 } from "lucide-react"
import { Promotion } from "../../../promo_creation/types"
import { format_pct } from "../../../promo_creation/planning_assumptions"
import { Button } from "../../../../../components/ui/button"
import {
  createPromotionPerformanceMetric,
  getPromotionEffectiveness,
  PromotionRankMetric,
  PromotionEffectivenessResponse,
} from "../../../../../api/promotion_effectiveness"
import { amount_or_cases, cases_value, compact_number, money_value, percent_value } from "./promotion_effectiveness_utils"
import { PromotionEffectivenessCharts } from "./PromotionEffectivenessCharts"
import { PromotionPerformanceUpload } from "./PromotionPerformanceUpload"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select"
import { EditableInsightKpi } from "./EditableInsightKpi"
import { PromotionPerformanceEditor } from "./PromotionPerformanceEditor"
import { default_metric_input, is_manual_metric_row, PerformanceEditorFocus } from "./promotion_performance_editor_utils"
import { usePromotionalContextAPI } from "../../../promo_creation/hooks/use_promotional_context_api"
import { toast } from "../../../../../components/ui/use-toast"

interface PromotionInsightsTabProps {
  promotion: Promotion
  on_switch_tab?: (tab: "edit" | "deductions" | "files" | "insights") => void
}

export function PromotionInsightsTab({ promotion, on_switch_tab }: PromotionInsightsTabProps) {
  const [data, set_data] = useState<PromotionEffectivenessResponse | null>(null)
  const [loading, set_loading] = useState(false)
  const [error, set_error] = useState<string | null>(null)
  const [rank_metric, set_rank_metric] = useState<PromotionRankMetric>("roi")
  const [editor_focus, set_editor_focus] = useState<PerformanceEditorFocus>("all")
  const editor_ref = useRef<HTMLDivElement>(null)
  const { handleUpdatePromotion } = usePromotionalContextAPI()

  async function load() {
    if (!promotion.id) return
    set_loading(true)
    set_error(null)
    try {
      set_data(await getPromotionEffectiveness(promotion.id, rank_metric))
    } catch (event) {
      set_error(event instanceof Error ? event.message : "Could not load insights")
    } finally {
      set_loading(false)
    }
  }

  useEffect(() => {
    load()
    // Reload when the planned amount changes (e.g. edited on the Edit tab) so the Planned KPI
    // and spend variance reflect the new value right away.
  }, [promotion.id, rank_metric, promotion.planned_amount])

  function open_metrics_editor(focus: PerformanceEditorFocus) {
    set_editor_focus(focus)
    editor_ref.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  async function handle_save_planned(amount: number | null) {
    const updated_promotion: Promotion = { ...promotion, planned_amount: amount ?? undefined }
    const saved = await handleUpdatePromotion(updated_promotion)
    if (!saved) {
      throw new Error("Could not update planned amount")
    }
    await load()
    toast({ title: "Planned spend updated", description: "Insights will refresh with the new planned amount." })
  }

  async function handle_save_sales_metrics(input: {
    period_start: string
    period_end: string
    baseline: number | null
    incremental: number | null
    total: number | null
  }) {
    if (!promotion.id) return
    const defaults = default_metric_input(promotion)
    await createPromotionPerformanceMetric(promotion.id, {
      ...defaults,
      period_start: input.period_start,
      period_end: input.period_end,
      baseline_sales_dollars: input.baseline,
      incremental_sales_dollars: input.incremental,
      total_sales_dollars: input.total,
    })
    await load()
    toast({ title: "Sales metrics saved", description: "Baseline and incremental sales were added." })
  }

  function handle_match_deductions() {
    on_switch_tab?.("deductions")
  }

  if (!promotion.id) {
    return <div class="p-6 text-sm text-gray-500">Save the promotion before viewing insights.</div>
  }

  if (loading && !data) {
    return (
      <div class="flex items-center justify-center p-12 text-sm text-gray-600">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading promotion insights...
      </div>
    )
  }

  if (error) {
    return (
      <div class="p-6">
        <div class="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
          <Button onClick={load} variant="outline" className="ml-3">Retry</Button>
        </div>
      </div>
    )
  }

  if (!data) return null

  const roi_label = data.summary.roi == null ? "Sales efficiency" : "ROI"
  // With plan-only data (e.g. Anaplan), show case volumes where dollar sales would go.
  const cases_only = data.summary.incremental_sales == null && data.summary.data_coverage.has_case_volumes
  const lift_label = data.summary.lift_is_unit_based ? "Planned lift" : "Lift"
  // The plan-time assumption sits beside the measured number rather than being replaced by it, so a
  // retailer with no history still shows what the planner expected.
  const expected_lift_note = promotion.expected_lift_pct != null
    ? `Expected at plan: ${format_pct(promotion.expected_lift_pct)}${promotion.participation_pct != null ? ` · ${format_pct(promotion.participation_pct)} of stores` : ""}`
    : undefined

  return (
    <div class="space-y-5 p-6">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Promotion Insights</h3>
          <p class="mt-1 text-sm text-gray-600">
            Spend, lift, ROI proxy, and peer performance from matched deductions, uploaded metrics, and syndicated reports.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Select value={rank_metric} onValueChange={(value: string) => set_rank_metric(value as PromotionRankMetric)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="roi">ROI rank</SelectItem>
              <SelectItem value="lift_pct">Lift rank</SelectItem>
              <SelectItem value="incremental_sales">Incremental sales</SelectItem>
              <SelectItem value="actual_spend">Actual spend</SelectItem>
              <SelectItem value="spend_variance">Spend variance</SelectItem>
            </SelectContent>
          </Select>
          <PromotionPerformanceUpload promotion_id={promotion.id} onImported={load} />
        </div>
      </div>

      <div class="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        <EditableInsightKpi
          label="Planned"
          value={money_value(data.summary.planned_spend)}
          kind="planned"
          planned_amount={promotion.planned_amount ?? null}
          on_save_planned={handle_save_planned}
        />
        <EditableInsightKpi
          label="Actual"
          value={money_value(data.summary.actual_spend)}
          tone={data.summary.spend_variance > 0 ? "bad" : "good"}
          kind="actual"
          on_match_deductions={handle_match_deductions}
        />
        <EditableInsightKpi
          label="Variance"
          value={money_value(data.summary.spend_variance)}
          tone={data.summary.spend_variance > 0 ? "bad" : "good"}
          kind="readonly"
        />
        {cases_only ? (
          <EditableInsightKpi
            label="Incremental cases"
            value={cases_value(data.summary.incremental_units)}
            kind="metrics"
            on_open_metrics={() => open_metrics_editor("sales")}
          />
        ) : (
          <EditableInsightKpi
            label="Incremental sales"
            value={money_value(data.summary.incremental_sales)}
            kind="metrics"
            on_open_metrics={() => open_metrics_editor("sales")}
          />
        )}
        <EditableInsightKpi
          label={lift_label}
          value={percent_value(data.summary.lift_pct)}
          note={expected_lift_note}
          kind="metrics"
          on_open_metrics={() => open_metrics_editor("sales")}
        />
        {cases_only && data.summary.roi == null && data.summary.sales_efficiency == null ? (
          <EditableInsightKpi
            label="Total cases (plan)"
            value={cases_value(data.summary.total_units)}
            kind="metrics"
            on_open_metrics={() => open_metrics_editor("sales")}
          />
        ) : (
          <EditableInsightKpi
            label={roi_label}
            value={compact_number(data.summary.roi ?? data.summary.sales_efficiency)}
            kind="metrics"
            on_open_metrics={() => open_metrics_editor(data.summary.roi == null ? "sales" : "margin")}
          />
        )}
      </div>

      {data.missing_data.length > 0 ? (
        <div class="rounded-md border border-amber-200 bg-amber-50 p-4">
          <div class="flex items-center gap-2 text-sm font-medium text-amber-900">
            <AlertCircle className="h-4 w-4" />
            More data would improve this read
          </div>
          <div class="mt-2 grid gap-1 text-sm text-amber-900 md:grid-cols-2">
            {data.missing_data.map((item: string) => (
              <div key={item}>{item}</div>
            ))}
          </div>
        </div>
      ) : (
        <div class="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4" />
          Core baseline, POS, unit, margin, and support metrics are available.
        </div>
      )}

      <PromotionEffectivenessCharts
        data={data}
        rank_metric={rank_metric}
        promotion={promotion}
        on_save_planned={handle_save_planned}
        on_save_sales_metrics={handle_save_sales_metrics}
        on_match_deductions={handle_match_deductions}
        on_edit_metrics={open_metrics_editor}
      />

      <div ref={editor_ref}>
        <PromotionPerformanceEditor
          promotion={promotion}
          metric_rows={data.metric_rows}
          focus={editor_focus}
          default_show_grid={data.metric_rows.some(is_manual_metric_row)}
          on_saved={load}
        />
      </div>

      <div class="rounded-md border border-gray-200">
        <div class="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
          <BarChart3 className="h-4 w-4 text-gray-500" />
          <h4 class="text-sm font-semibold text-gray-900">Metric rows</h4>
        </div>
        {data.metric_rows.length === 0 ? (
          <div class="p-4 text-sm text-gray-500">No structured performance metrics yet. Use the form above or upload a CSV.</div>
        ) : (
          <div class="max-h-64 overflow-auto">
            <table class="w-full text-sm">
              <thead class="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th class="px-4 py-2">Period</th>
                  <th class="px-4 py-2">Source</th>
                  <th class="px-4 py-2">Product</th>
                  <th class="px-4 py-2">Baseline</th>
                  <th class="px-4 py-2">Incremental</th>
                  <th class="px-4 py-2">Margin</th>
                  <th class="px-4 py-2">Support</th>
                  <th class="px-4 py-2">Match</th>
                </tr>
              </thead>
              <tbody>
                {data.metric_rows.map((row) => (
                  <tr key={row.id} class="border-t border-gray-100">
                    <td class="px-4 py-2">{row.period_start}</td>
                    <td class="px-4 py-2">{source_label(row.source_type)}</td>
                    <td class="px-4 py-2">{row.product || "All"}</td>
                    <td class="px-4 py-2">{amount_or_cases(row.baseline_sales_dollars, row.baseline_units)}</td>
                    <td class="px-4 py-2">{amount_or_cases(row.incremental_sales_dollars, row.incremental_units)}</td>
                    <td class="px-4 py-2">{money_value(row.incremental_margin_dollars)}</td>
                    <td class="px-4 py-2">{support_text(row.feature_support, row.display_support)}</td>
                    <td class="px-4 py-2">{match_text(row.match_confidence, row.match_reason)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function support_text(feature?: boolean | null, display?: boolean | null): string {
  if (feature && display) return "Feature + display"
  if (feature) return "Feature"
  if (display) return "Display"
  return "None"
}

function source_label(source_type: string): string {
  switch (source_type) {
    case "syndicated_report":
      return "Syndicated"
    case "generated_sample":
      return "Syndicated"
    case "demo_generated":
      return "Syndicated"
    case "anaplan_plan":
      return "Anaplan plan"
    default:
      return "Manual"
  }
}

function match_text(confidence?: number | null, reason?: string | null): string {
  if (confidence == null) return reason || "-"
  return `${Math.round(confidence * 100)}%${reason ? ` - ${reason}` : ""}`
}
